import Modal from '@components/Modals/Modal.js';
import { IS_APP } from '@constants/global';
import { PaperClipIcon } from '@icons/IconLibrary';
import { route } from '@router/index';
import { UIDiv, UIImage, UIText } from '@ui';

export default class AttachedFilesModal {
  constructor({ task, onFileSelect = null }) {
    this.task = task;
    this.onFileSelect = onFileSelect;
    this.modal = null;
  }

  show() {
    const content = this.#renderContent();

    this.modal = new Modal('Attached Files', {
      content: content,
      showCancel: false,
      showConfirm: false,
      showCloseIcon: true,
      modalContentClass: 'max-w-md',
      contentContainerClass: 'max-h-[70vh] overflow-y-auto p-0',
      canClose: true,
    });

    return this.modal.show();
  }

  #renderContent() {
    const container = new UIDiv();

    if (!this.task.reconstructions || this.task.reconstructions.length === 0) {
      container.add(this.#renderEmptyState());
    } else {
      container.add(this.#renderFilesList());
    }

    return container;
  }

  #renderFilesList() {
    const container = new UIDiv();
    container.addClass('p-6');

    // Files list
    const filesList = new UIDiv();
    filesList.addClass('space-y-3');

    this.task.reconstructions.forEach((reconstruction, index) => {
      const fileItem = this.#createFileItem(reconstruction, index);
      filesList.add(fileItem);
    });

    container.add(filesList);
    return container;
  }

  #createFileItem(reconstruction, index) {
    const item = new UIDiv();
    item.addClass(
      'flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all duration-200 group',
    );

    // File icon/thumbnail
    const iconContainer = new UIDiv();
    iconContainer.addClass(
      'w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex-shrink-0 flex items-center justify-center',
    );

    if (reconstruction.thumbnail) {
      const thumbnail = new UIImage(reconstruction.thumbnail);
      thumbnail.addClass('w-full h-full object-cover');
      iconContainer.add(thumbnail);
    } else {
      const fileExtension = this.#getFileExtension(reconstruction);
      const extensionText = new UIText(
        fileExtension ? fileExtension.toUpperCase() : 'FILE',
      );
      extensionText.addClass('text-xs font-bold text-gray-600');
      iconContainer.add(extensionText);
    }

    // File info
    const fileInfo = new UIDiv();
    fileInfo.addClass('flex-1 min-w-0');

    const fileName = new UIText(
      reconstruction.fileOriginalName ||
        reconstruction.fileName ||
        reconstruction.modelName ||
        `File ${index + 1}`,
    );
    fileName.addClass('font-medium text-gray-900 text-sm truncate block mb-1');

    const fileDetails = new UIDiv();
    fileDetails.addClass('flex items-center gap-2 text-xs text-gray-500');

    // File size
    if (reconstruction.fileSize) {
      const fileSize = new UIText(this.formatFileSize(reconstruction.fileSize));
      fileDetails.add(fileSize);
    }

    // File type
    const fileExtension = this.#getFileExtension(reconstruction);
    if (fileExtension) {
      if (reconstruction.fileSize) {
        const separator = new UIText('•');
        separator.addClass('text-gray-300');
        fileDetails.add(separator);
      }

      const fileType = new UIText(`${fileExtension.toUpperCase()} file`);
      fileDetails.add(fileType);
    }

    fileInfo.add(fileName, fileDetails);

    // Action button
    const actionButton = new UIDiv();
    actionButton.addClass(
      'flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-105',
    );

    const actionText = new UIText('Open');
    actionButton.add(actionText);

    item.add(iconContainer, fileInfo, actionButton);

    // Click handler
    item.onClick(() => {
      this.#handleFileClick(reconstruction);
    });

    return item;
  }

  #renderEmptyState() {
    const container = new UIDiv();
    container.addClass('p-12 text-center');

    const icon = new UIDiv();
    icon.addClass(
      'w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center',
    );

    const paperclipIcon = new PaperClipIcon({
      size: 24,
      color: '#9CA3AF',
    });
    icon.add(paperclipIcon);

    const message = new UIText('No files attached');
    message.addClass('text-gray-600 text-sm font-medium mb-2');

    const description = new UIText('This task has no attached files');
    description.addClass('text-gray-400 text-xs');

    container.add(icon, message, description);
    return container;
  }

  #handleFileClick(reconstruction) {
    if (this.onFileSelect) {
      this.onFileSelect(reconstruction);
    } else {
      this.#openTaskWithFile(reconstruction);
    }

    // Close the modal
    if (this.modal) {
      this.modal.close(true);
    }
  }

  #openTaskWithFile(reconstruction) {
    const params = new URLSearchParams(window.location.search);
    params.set('sharingId', this.task.sharingId);
    params.set('from', 'projects');
    params.set('pId', this.task.parentId);
    if (IS_APP()) {
      params.set('isApp', 'true');
    }
    if (reconstruction.reconstructionID) {
      params.set('reconstructionId', reconstruction.reconstructionID);
    }
    const newSearch = params.toString();
    const newUrl = `/viewer?${newSearch}`;
    route(newUrl);
  }

  #getFileExtension(reconstruction) {
    const fileName =
      reconstruction.fileOriginalName ||
      reconstruction.fileName ||
      reconstruction.filePath ||
      '';
    const match = fileName.match(/\.([^.]+)$/);
    return match ? match[1] : null;
  }

  // to be replace later by an utils function I made for the assembly features (will be merged later)
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  destroy() {
    if (this.modal) {
      this.modal.close(null);
      this.modal = null;
    }
  }
}
